import type React from 'react';
import { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { projectService } from '../services/projectService';
import { getLogProjects } from '../services/timeEntryService';
import { colors } from '../theme/colors';
import { useAuth } from '../contexts/AuthContext';
import { RefreshCw } from 'lucide-react';
import { GroupReportTable } from './GroupReportTable';
import { IsolatedReportTable } from './IsolatedReportTable';

interface CustomerReportProps {
  selectedProjectId?: string;
  onProjectChange?: (projectId: string) => void;
  initialProjectId?: string;
}

export const CustomerReport: React.FC<CustomerReportProps> = ({
  selectedProjectId: propSelectedProjectId,
  onProjectChange,
  initialProjectId
}) => {
  const { user } = useAuth();
  const userRole = user?.role?.toLowerCase().trim() || '';
  const isAdmin = userRole === 'admin';

  const [internalSelectedProjectId, setInternalSelectedProjectId] = useState<string>(initialProjectId || '');
  const selectedProjectId = propSelectedProjectId !== undefined ? propSelectedProjectId : internalSelectedProjectId;
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [reportType, setReportType] = useState<'group' | 'isolated'>('group');
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [isolatedSelectedCount, setIsolatedSelectedCount] = useState<number>(0);
  const [selectedOrder, setSelectedOrder] = useState<string>('__NOT_APPLICABLE__');
  const [customOrderNumber, setCustomOrderNumber] = useState<string>('');

  const {
    data: projects = [],
    isLoading: isLoadingProjects,
  } = useQuery({
    queryKey: ['projects', 'by-role'],
    queryFn: () => (isAdmin ? projectService.getProjects() : projectService.getProjectsByRole()),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const externalProjects = useMemo(() => {
    return projects.filter((p) => p.type === 'External');
  }, [projects]);

  const selectedProject = useMemo(() => {
    return externalProjects.find((p) => p.id === selectedProjectId) ?? externalProjects[0];
  }, [externalProjects, selectedProjectId]);

  useEffect(() => {
    setShowPreview(false);
    setSelectedOrder('__NOT_APPLICABLE__');
    setCustomOrderNumber('');
  }, [reportType, selectedProjectId, selectedMonth]);

  useEffect(() => {
    if (selectedOrder !== '__NOT_APPLICABLE__') {
      setCustomOrderNumber('');
    }
  }, [selectedOrder]);

  const queryClient = useQueryClient();
  const {
    data: logProjectsData = [],
  } = useQuery({
    queryKey: ['log-projects'],
    queryFn: () => getLogProjects(),
    enabled: isAdmin && reportType === 'group' && !!selectedProject && !!selectedMonth,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const onRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['projects', 'by-role'] });
    queryClient.invalidateQueries({ queryKey: ['log-projects'] });
    queryClient.invalidateQueries({ queryKey: ['customer-report-group'] });
    queryClient.invalidateQueries({ queryKey: ['customer-report-isolated-employees'] });
    queryClient.invalidateQueries({ queryKey: ['customer-report-isolated'] });
  };

  const parseDate = (dateStr: string): Date | null => {
    try {
      const parts = dateStr.split('.');
      if (parts.length !== 3) return null;
      return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    } catch {
      return null;
    }
  };

  const getMonthYearKey = (date: Date): string => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  const availableOrders = useMemo(() => {
    if (!selectedProject || !selectedMonth || logProjectsData.length === 0) return [];

    const filtered = logProjectsData.filter((item) => {
      const projectMatch = item.project.match(/^([^\s]+)/);
      const projectNumber = projectMatch ? projectMatch[1] : '';
      if (projectNumber !== selectedProject.project_number) return false;

      const date = parseDate(item.date);
      if (!date) return false;
      return getMonthYearKey(date) === selectedMonth;
    });

    const orderSet = new Set<string>();
    filtered.forEach((item) => {
      if (item.order && item.order !== '-') {
        orderSet.add(item.order);
      }
    });
    return Array.from(orderSet).sort();
  }, [logProjectsData, selectedProject, selectedMonth]);

  const formatMonthYear = (monthYear: string): string => {
    const [year, month] = monthYear.split('-');
    const monthIndex = parseInt(month, 10) - 1;
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return `${monthNames[monthIndex]} ${year}`;
  };

  const generateMonthOptions = (): string[] => {
    const options: string[] = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentYear, currentMonth - 1 - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      options.push(`${year}-${String(month).padStart(2, '0')}`);
    }

    return options.reverse();
  };

  const monthOptions = generateMonthOptions();

  if (isLoadingProjects) {
    return (
      <div
        style={{
          backgroundColor: colors.neutral.white,
          borderRadius: '8px',
          padding: '2rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
            color: colors.neutral.grey[600],
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              border: `4px solid ${colors.neutral.grey[200]}`,
              borderTop: `4px solid ${colors.accent.blue}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div
        style={{
          backgroundColor: colors.neutral.white,
          borderRadius: '8px',
          padding: '2rem',
          textAlign: 'center',
          color: colors.neutral.grey[600],
        }}
      >
        <p>No projects available. You need to have a role (Manager, Assistant, Deputy, or Finance) in at least one project to access Customer Reports.</p>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: colors.neutral.white,
        padding: '1rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              alignItems: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ width: '450px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: colors.neutral.grey[700],
                }}
              >
                Project
              </label>
              <select
                value={selectedProject?.id ?? ''}
                onChange={(e) => {
                  if (onProjectChange) {
                    onProjectChange(e.target.value);
                  } else {
                    setInternalSelectedProjectId(e.target.value);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: `1px solid ${colors.neutral.grey[300]}`,
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  backgroundColor: colors.neutral.white,
                  color: colors.secondary.text,
                }}
              >
                {externalProjects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.project_number} - {project.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ minWidth: '200px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: colors.neutral.grey[700],
                }}
              >
                Month/Year
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: `1px solid ${colors.neutral.grey[300]}`,
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  backgroundColor: colors.neutral.white,
                  color: colors.secondary.text,
                }}
              >
                {monthOptions.map((month) => (
                  <option key={month} value={month}>
                    {formatMonthYear(month)}
                  </option>
                ))}
              </select>
            </div>

            {selectedProject && selectedMonth && (
              <div style={{ minWidth: '200px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: colors.neutral.grey[700],
                  }}
                >
                  Bestellnummer
                </label>
                <select
                  value={selectedOrder}
                  onChange={(e) => setSelectedOrder(e.target.value)}
                  disabled={!isAdmin || availableOrders.length === 0}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: `1px solid ${colors.neutral.grey[300]}`,
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    backgroundColor: isAdmin && availableOrders.length > 0 ? colors.neutral.white : colors.neutral.grey[100],
                    color: colors.secondary.text,
                    cursor: isAdmin && availableOrders.length > 0 ? 'pointer' : 'not-allowed',
                  }}
                >
                  <option value="__NOT_APPLICABLE__">Not Applicable</option>
                  {availableOrders.map((order) => (
                    <option key={order} value={order}>
                      {order}
                    </option>
                  ))}
                </select>

              </div>
            )}

            <div style={{ minWidth: '200px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: colors.neutral.grey[700],
                }}
              >
                Report Type
              </label>
              <div
                style={{
                  display: 'flex',
                  gap: '1rem',
                  alignItems: 'center',
                  minHeight: '2.5rem',
                  paddingTop: '0.125rem',
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="reportType"
                    value="group"
                    checked={reportType === 'group'}
                    onChange={(e) => setReportType(e.target.value as 'group' | 'isolated')}
                  />
                  Group
                </label>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="reportType"
                    value="isolated"
                    checked={reportType === 'isolated'}
                    onChange={(e) => setReportType(e.target.value as 'group' | 'isolated')}
                  />
                  Isolated
                </label>
              </div>
            </div>


          </div>

          {(reportType === 'group' || reportType === 'isolated') && selectedProject && selectedMonth && (

            <button
              onClick={() => setShowPreview(!showPreview)}
              disabled={reportType === 'isolated' && isolatedSelectedCount === 0}
              style={{
                backgroundColor: showPreview ? colors.neutral.grey[400] : colors.accent.blue,
                color: colors.neutral.white,
                border: 'none',
                borderRadius: '6px',
                padding: '0.7rem 1.2rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: reportType === 'isolated' && isolatedSelectedCount === 0 ? 'not-allowed' : 'pointer',
                opacity: reportType === 'isolated' && isolatedSelectedCount === 0 ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s ease',
                height: 'fit-content',
              }}
              onMouseEnter={(e) => {
                if (reportType === 'isolated' && isolatedSelectedCount === 0) return;
                e.currentTarget.style.backgroundColor = showPreview ? colors.neutral.grey[500] : colors.accent.blue + 'dd';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = showPreview ? colors.neutral.grey[400] : colors.accent.blue;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {showPreview ? 'Edit Report' : reportType === 'isolated' ? 'Preview All' : 'Preview Report'}
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', height: '100%',marginTop: '0.5rem',}}>

          {selectedProject && selectedMonth && selectedOrder === '__NOT_APPLICABLE__' && (
            <input
              type="text"
              value={customOrderNumber}
              onChange={(e) => setCustomOrderNumber(e.target.value)}
              placeholder="Enter custom order number"
              style={{
                width: '450px',
                padding: '0.5rem',
                
                border: `1px solid ${colors.neutral.grey[300]}`,
                borderRadius: '6px',
                fontSize: '0.875rem',
                backgroundColor: colors.neutral.white,
                color: colors.secondary.text,
              }}
            />
          )}
          <button
            type="button"
            onClick={onRefresh}
            style={{
              backgroundColor: colors.neutral.white,
              color: colors.accent.blue,
              border: `1px solid ${colors.accent.blue}`,
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s ease',
              height: '100%',
              gap: '0.5rem',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.neutral.grey[100];
              e.currentTarget.style.borderColor = colors.neutral.grey[400];
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.neutral.white;
              e.currentTarget.style.borderColor = colors.neutral.grey[300];
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {selectedProject && (
        <>
          {reportType === 'group' ? (
            <GroupReportTable
              projectNumber={selectedProject.project_number}
              projectName={selectedProject.name}
              month={selectedMonth}
              showPreview={showPreview}
              selectedOrder={selectedOrder}
              customOrderNumber={customOrderNumber}
            />
          ) : (
            <IsolatedReportTable
              projectNumber={selectedProject.project_number}
              projectName={selectedProject.name}
              month={selectedMonth}
              selectedOrder={selectedOrder}
              customOrderNumber={customOrderNumber}
              showPreview={showPreview}
              onBackFromPreview={() => setShowPreview(false)}
              onSelectionChange={setIsolatedSelectedCount}
            />
          )}
        </>
      )}
    </div>
  );
};
